// ============================================
// MainLayout Component
// Main layout wrapper with Sidebar and TopNavBar
// ============================================

import { useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopNavBar } from './TopNavBar';
import { ToastContainer } from '../shared';
import { useStores } from '../../hooks';
import { useStoreSelectionStore } from '../../stores';
import type { Store } from '@conai/shared-types';

// ============================================
// Component
// ============================================

export function MainLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const { setAvailableStores } = useStoreSelectionStore();

  const { data: storesData } = useStores({
    page: 1,
    page_size: 100,
  });

  const storesList: Store[] = useMemo(() => {
    const data = storesData as
      | {
          results?: Store[];
          items?: Store[];
          data?: { results?: Store[]; items?: Store[] };
        }
      | undefined;

    return data?.results ?? data?.items ?? data?.data?.results ?? data?.data?.items ?? [];
  }, [storesData]);

  // Auto-select the first store (store picker modal removed) — the selection
  // store picks the first available store automatically when the list loads.
  useEffect(() => {
    if (storesList.length > 0) {
      setAvailableStores(storesList);
    }
  }, [storesList, setAvailableStores]);

  const handleOpenSidebar = () => setIsSidebarOpen(true);
  const handleCloseSidebar = () => setIsSidebarOpen(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Sidebar */}
      <Sidebar isOpen={isSidebarOpen} onClose={handleCloseSidebar} />

      {/* Top Navigation */}
      <TopNavBar onMenuClick={handleOpenSidebar} />

      {/* Main Content Area */}
      <main className="overflow-x-hidden lg:ml-64">
        {/* Content container with padding for top nav */}
        <div className="min-h-screen pt-16">
          <Outlet />
        </div>
      </main>

      <ToastContainer />
    </div>
  );
}
